import { useFamilyGroupMemberStore } from "@/gp/FamilyGroupMemberStore"
import { useFamilyGroupInviteStore } from "@/gp/FamilyGroupInviteStore"
import { useDataGroupMemberStore } from "@/gp/DataGroupMemberStore"
import { useDataGroupStore } from "@/gp/DataGroupStore"
import { useUserStore } from "@/gp/UserStore"
import { useTokenManager } from "@/auth/TokenManager"
import { uniq, uniqBy } from "lodash"

export interface SharingUser {
  userId?: string
  inviteId?: string
  displayName: string
  invited: boolean
  address?: string
}

export function useSharingLimiter() {
    const familyGroupMemberStore = useFamilyGroupMemberStore()
    const familyGroupInviteStore = useFamilyGroupInviteStore()
    const dataGroupMemberStore = useDataGroupMemberStore()
    const dataGroupStore = useDataGroupStore()
    const userStore = useUserStore()
    const tokenManager = useTokenManager()

    async function getSharingAsync() {
        // user members and pending invites for a FG count toward a FG owner's sharing limit ONLY if at least one
        // "limited sharing" RG is shared with the FG. A RG is considered "limited sharing" if the RG's owner has
        // a "limited sharing" plan (which transfers responsibility for sharing to the FG owner)

        const currentUserId = tokenManager.current.userId
        const currentUser = await userStore.getAsyncUser(currentUserId)?.whenLoadCompleted
        const userMembers = await familyGroupMemberStore.getMembersForUsersAsync([currentUserId])

        const ownedFamilyGroupIds = userMembers.filter(fm => fm.isOwner).map(fm => fm.familyGroupId)
        const ownedFamilyGroupDataGroupMembers = await dataGroupMemberStore.getMembersForFamilyGroupsAsync(ownedFamilyGroupIds)

        const sharedDataGroupIds = uniq(ownedFamilyGroupDataGroupMembers.filter(dm => !dm.disabled).map(dm => dm.dataGroupId))
        const sharedDataGroups = await dataGroupStore.getGroupListAsync(sharedDataGroupIds)
        const sharedResearchGroups = sharedDataGroups.filter(dg => !dg.isProfile && !dg.disabled)

        const sharedResearchGroupOwnerIds = uniq(sharedResearchGroups.map(dg => dg.ownerId!))
        const sharedResearchGroupOwners = await userStore.getUserListAsync(sharedResearchGroupOwnerIds)

        const limitedOwnerIds = new Set(sharedResearchGroupOwners.filter(u => u.isSharingLimited).map(u => u.id!))
        const limitedSharingResearchGroupIds = new Set(sharedResearchGroups.filter(dg => limitedOwnerIds.has(dg.ownerId!)).map(dg => dg.id!))
        const limitedSharingOwnedFamilyGroupIds = ownedFamilyGroupDataGroupMembers
            .filter(dm => limitedSharingResearchGroupIds.has(dm.dataGroupId!))
            .map(dm => dm.principalId!)

        const ownedGroupMembers = await familyGroupMemberStore.getMembersForFamilyGroupsAsync(limitedSharingOwnedFamilyGroupIds)
        const ownedGroupInvites = await familyGroupInviteStore.getInvitesForFamilyGroupsAsync(limitedSharingOwnedFamilyGroupIds)

        return {
            members: ownedGroupMembers.filter(fm => fm.userId != currentUserId), // only count OTHER users
            invites: ownedGroupInvites.filter(inv => inv.isPending && 
                inv.recipientUserId != currentUserId && 
                inv.recipientEmail != currentUser!.email), // only count PENDING invites to OTHER users
        }
    }

    async function getSharingUsersAsync() {
        const sharing = await getSharingAsync()

        const memberUserIds = new Set(sharing.members.map(m => m.userId))
        const invitedUserIds = new Set(sharing.invites.filter(n => n.recipientUserId).map(n => n.recipientUserId))
        const uniqueUserIds = new Set([...memberUserIds, ...invitedUserIds])

        const users = await userStore.getUserListAsync([...uniqueUserIds])
        const sharingUsers = users.map(u => ({
            userId: u.id!,
            displayName: u.displayName,
            invited: (invitedUserIds.has(u.id) && !memberUserIds.has(u.id)),
        })) as SharingUser[]
        
        const invitedAddresses = sharing.invites.filter(n => !n.recipientUserId).map(n => ({ 
            id: n.id!,
            displayName: n.recipientDisplayName,
            invited: true,
            address: n.recipientEmail
        })) as SharingUser[]

        sharingUsers.push(...uniqBy(invitedAddresses, 'address')) // if duplicates, id and displayName will be arbitrary

        return sharingUsers
    }

    return {
        getSharingAsync,
        getSharingUsersAsync,
    }
}
